import { useCallback, useContext, useMemo } from 'react'
import useNavigation from './useNavigation'
import { useSearchParams } from 'react-router-dom';
import { ScenarioStatesProviderContext } from '../providers/StatesProvider';
import { getStepsWithUnsavedChanges, validateSameScenarioData } from '../utils/validation';
import { SCENARIO_STEPS, SCENARIO_STEPS_ORDER, SCENARIO_OPERATION_TYPES, SCENARIO_DEPLOYMENT_TYPES, SCENARIO_STATUS } from '../../constants';
import useLoader from './useLoader';
import {
    getScenarioSetupStatus,
    isOnboardScheduleGenerateRunning,
    isSatelliteGroupsAddRunning
} from '../utils/satellite-groups-add';

const useStepper = () => {
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useStepper must be used within a Scenario States Provider')
    }
    const { scenarioResponse, formData, orbitAnimationDetails } = context
    const { step } = useNavigation()
    const { presimRunning, loading } = useLoader()
    const [searchParams, setSearchParams] = useSearchParams();

    const animationRunning = useMemo(() => orbitAnimationDetails.startAnimation, [orbitAnimationDetails.startAnimation])
    const baseLoading = useMemo(() => loading.base, [loading.base])
    const setupStatus = useMemo(() => getScenarioSetupStatus(scenarioResponse), [scenarioResponse])
    const asyncSetupRunning = useMemo(
        () => formData.assets.schedule.status === 'running' ||
            isSatelliteGroupsAddRunning(setupStatus) ||
            isOnboardScheduleGenerateRunning(setupStatus),
        [formData.assets.schedule.status, setupStatus]
    )
    const disabled = useMemo(
        () => animationRunning || presimRunning || asyncSetupRunning || Object.keys(loading).some(key => loading[key]),
        [loading, presimRunning, animationRunning, asyncSetupRunning]
    )

    const ValidateSteps = Object.fromEntries(
        SCENARIO_STEPS_ORDER.map((step, index) => [
            step,
            SCENARIO_STEPS_ORDER.slice(index) // include current + following steps
        ])
    ) || [];
    const DisabledSteps = useCallback(() => {
        const unSavedStep = getStepsWithUnsavedChanges(formData, scenarioResponse)
        if (!unSavedStep) return []
        const stepIndex = SCENARIO_STEPS_ORDER.indexOf(unSavedStep)
        return SCENARIO_STEPS_ORDER.slice(stepIndex + 1)
    }, [formData, scenarioResponse])

    const checkSameData = useMemo(() => validateSameScenarioData(step as string, formData, scenarioResponse), [step, formData, scenarioResponse])

    const updateScenarioStep = useCallback((newStep: string, scenarioId?: string) => {
        searchParams.set('step', newStep);
        if (scenarioId) searchParams.set('scenarioId', scenarioId);
        setSearchParams(searchParams);
    }, [searchParams, setSearchParams]);

    const checkDisabledSteps = useCallback((componentName: string) => {
        if (!step) return false
        else if (disabled) return true
        const hasGroups = scenarioResponse?.satelliteGroups && scenarioResponse?.satelliteGroups?.length > 0
        const hasHwilConfig = scenarioResponse?.hwInfo && scenarioResponse?.hwInfo?.length > 0
        const scenarioDeploymentType = scenarioResponse?.scenarioDeploymentType
        const scenarioOperation = scenarioResponse?.scenarioOperation
        const hasGroundStations = scenarioResponse?.groundStations && scenarioResponse?.groundStations?.length > 0
        const hasSegmentPreviousStepData = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? hasHwilConfig : hasGroups
        const hasSchedulePreviousStepData = scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP ? hasGroups : hasGroundStations

        switch (componentName) {
            case SCENARIO_STEPS.DETAILS:
                return DisabledSteps().includes(SCENARIO_STEPS.DETAILS)
            case SCENARIO_STEPS.SATELLITES:
                return DisabledSteps().includes(SCENARIO_STEPS.SATELLITES) ? true : scenarioResponse ? false : true
            case SCENARIO_STEPS.INITIAL_ALTITUDE:
                return DisabledSteps().includes(SCENARIO_STEPS.INITIAL_ALTITUDE) ? true : hasGroups ? false : true
            case SCENARIO_STEPS.HWIL:
                return DisabledSteps().includes(SCENARIO_STEPS.HWIL) ? true : hasGroups ? false : true
            case SCENARIO_STEPS.SEGMENTS:
                return DisabledSteps().includes(SCENARIO_STEPS.SEGMENTS) ? true : hasSegmentPreviousStepData ? false : true
            case SCENARIO_STEPS.FEATURES:
                return false
            case SCENARIO_STEPS.SCHEDULE:
                return DisabledSteps().includes(SCENARIO_STEPS.SCHEDULE) ? true : hasSchedulePreviousStepData ? false : true
            case SCENARIO_STEPS.FAULTS:
                return DisabledSteps().includes(SCENARIO_STEPS.FAULTS) ? true : formData?.assets?.schedule?.status === 'completed' ? false : true
            case SCENARIO_STEPS.ASSETS:
                return DisabledSteps().includes(SCENARIO_STEPS.ASSETS) ? true : formData?.assets?.schedule?.status === 'completed' ? false : true
            default:
                return false
        }
    }, [step, DisabledSteps, checkSameData, scenarioResponse, disabled, SCENARIO_STEPS, formData])

    const checkActiveStep = useCallback((stepName: string) => {
        if (!baseLoading && !animationRunning && (step === stepName)) return true
        return false
    }, [step, baseLoading, animationRunning])

    const checkStepCompleted = useCallback((componentName: string) => {
        if (!step) return false
        switch (componentName) {
            case SCENARIO_STEPS.DETAILS:
                return checkSameData && scenarioResponse ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.DETAILS)
            case SCENARIO_STEPS.SATELLITES:
                return checkSameData && (scenarioResponse?.satelliteGroups && scenarioResponse?.satelliteGroups?.length > 0) ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.SATELLITES)
            case SCENARIO_STEPS.INITIAL_ALTITUDE:
                return checkSameData && (scenarioResponse?.intialAttitudeSet || scenarioResponse?.satelliteGroups && scenarioResponse?.satelliteGroups?.length > 0) ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.INITIAL_ALTITUDE)
            case SCENARIO_STEPS.HWIL:
                return checkSameData && (scenarioResponse?.hwInfo && scenarioResponse?.hwInfo?.length > 0) ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.HWIL)
            case SCENARIO_STEPS.SEGMENTS:
                return checkSameData && (scenarioResponse?.groundStations && scenarioResponse?.groundStations?.length > 0) ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.SEGMENTS)
            case SCENARIO_STEPS.FEATURES:
                return false
            case SCENARIO_STEPS.SCHEDULE:
                return checkSameData && formData?.assets?.schedule?.status === 'completed' ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.SCHEDULE)
            case SCENARIO_STEPS.FAULTS:
                return checkSameData && formData?.assets?.faultPlans?.status === 'completed' ? true : !ValidateSteps[step].includes(SCENARIO_STEPS.FAULTS)
            case SCENARIO_STEPS.ASSETS:
                const scenarioDeploymentType = scenarioResponse?.scenarioDeploymentType
                const hasHwilAssets = formData?.assets.hwilAssets?.status === 'completed'
                return scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? hasHwilAssets : step === SCENARIO_STEPS.DEPLOY
            default:
                return false
        }
    }, [step, baseLoading, checkSameData, scenarioResponse, formData, SCENARIO_STEPS, ValidateSteps])

    return {
        updateScenarioStep,
        checkActiveStep,
        checkStepCompleted,
        checkDisabledSteps
    }
}

export default useStepper
